import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import VisionAiWizard from "@/components/VisionAiWizard";
import { getVisionBannerDismissed, setVisionBannerDismissed } from "@/lib/safeStorage";
import { Sparkles, X, CheckCircle2 } from "lucide-react";

/**
 * Sprint 1 Vision AI — opt-in banner shown to partner/sales/admin users
 * whose profile is not yet complete. Dismissable (per-browser localStorage
 * flag); the banner still surfaces when profile is completed to show the
 * active-state confirmation the FIRST time. Simple users see nothing.
 */
export default function VisionAiOptInBanner({ testId = "vision-optin-banner" }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    if (!["partner", "sales", "admin"].includes(user.role)) return;
    api
      .get("/vision/profile")
      .then((r) => setProfile(r.data || null))
      .catch(() => setProfile(null));
  }, [user]);

  useEffect(() => {
    setDismissed(getVisionBannerDismissed());
    load();
  }, [load]);

  if (!user) return null;
  if (!["partner", "sales", "admin"].includes(user.role)) return null;
  if (!profile) return null;

  const done = Object.entries(profile)
    .filter(([k]) => ["preferred_style", "ornament_density", "source_fidelity", "target_audience", "mood_light"].includes(k))
    .filter(([, v]) => Boolean(v)).length;

  const isComplete = profile.is_complete;

  // If the user completed AND dismissed the confirmation, hide entirely
  if (isComplete && dismissed) return null;
  // If the profile is not complete and the user dismissed the banner, still hide it — they'll find it in Account
  if (!isComplete && dismissed) return null;

  const onDismiss = () => {
    setVisionBannerDismissed(true);
    setDismissed(true);
  };

  return (
    <>
      <div
        className={`relative rounded-lg border p-5 sm:p-6 mb-6 overflow-hidden ${
          isComplete
            ? "border-emerald-500/30 bg-emerald-500/5"
            : "border-orange-500/30 bg-orange-500/5"
        }`}
        data-testid={testId}
        data-state={isComplete ? "complete" : "pending"}
      >
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-3 right-3 text-zinc-500 hover:text-white transition p-1 rounded"
          aria-label={t("vision_ai.banner_dismiss")}
          data-testid="vision-optin-dismiss"
        >
          <X size={16} strokeWidth={1.5} />
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className={`text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2 ${
              isComplete ? "text-emerald-400" : "text-orange-500"
            }`}>
              {isComplete ? (
                <CheckCircle2 size={12} strokeWidth={2} />
              ) : (
                <Sparkles size={12} strokeWidth={1.5} />
              )}
              {t("vision_ai.banner_eyebrow")}
            </div>
            <h3 className="font-outfit font-bold text-white text-lg tracking-tight">
              {isComplete
                ? t("vision_ai.banner_complete")
                : t("vision_ai.banner_title")}
            </h3>
            {!isComplete && (
              <p className="mt-1 text-sm text-zinc-400 max-w-2xl">
                {t("vision_ai.banner_body")}
              </p>
            )}
            {done > 0 && !isComplete && (
              <div className="mt-2 text-xs text-orange-400" data-testid="vision-optin-progress">
                {t("vision_ai.banner_progress", { done })}
              </div>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              data-testid="vision-optin-cta"
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                isComplete
                  ? "bg-zinc-900 border border-white/10 text-white hover:bg-zinc-800"
                  : "bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20"
              }`}
            >
              <Sparkles size={14} strokeWidth={1.5} />
              {isComplete ? t("vision_ai.banner_cta_edit") : t("vision_ai.banner_cta")}
            </button>
          </div>
        </div>
      </div>
      <VisionAiWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onSaved={() => {
          // Reset dismissed flag so the confirmation banner shows once,
          // then re-fetch the new completed state
          setVisionBannerDismissed(false);
          setDismissed(false);
          load();
        }}
      />
    </>
  );
}
